import React, { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import Header from '../Header/header';
import Footer from '../Footer/footer';
import getPurchase from '../../services/purchase/getPurchase';
import { getAllCompanyStories } from '../../services/purchase/getCompanyStories';
import bulkTransferStories from '../../services/purchase/bulkTransferStories';
import { PURCHASE_STATUS } from '../../utils/purchasePublication';
import cookie from '../../cookieHelper/cookie';
import { auth as AuthObject } from '../../services/login';
import toastNotify from '../../utils/toastNotifier';
import calculateDateInFormat from '../../utils/calculateDate';
import EmptyScreen from '../../components/EmptyScreen/EmptyScreen';
import '../storyListing/storyListingFooter.scss';
import '../storyListing/storyListingTable.scss';
import '../Header/header.scss';

const PAGE_SIZE = 10;
const MAX_SELECTION = 50;
const AUTH_ERROR_CODES = [ '10411', '10412', '10416', '10417' ];
const STORY_STATUS = { 0: 'Deleted', 1: 'Active', 2: 'Expired', 3: 'Saved as Draft' };

function isAuthError(err) {
    return err.response?.status === 401 || AUTH_ERROR_CODES.includes(String(err.response?.data?.status?.code));
}

// A story can be imported only from another purchase of the same company on the same portal.
function importBlockReason(story, purchase, centralCompanyId) {
    if (!story?.storyId) return 'Story ID is missing.';
    if (!story.purchaseId) return 'Story is not linked to a purchase.';
    if (String(story.purchaseId) === String(purchase._id)) return 'Story already belongs to this purchase.';
    if (String(story.centralCompanyId) !== String(centralCompanyId)) return 'Story belongs to another company.';
    if (!(story.publishOn || []).includes(purchase.origin)) return `Story is not published on ${purchase.origin}.`;
    return '';
}

function currentUser() {
    try {
        const profile = JSON.parse(cookie.getCookie('loginProfileInfo'));
        return profile?.email || profile?.id || '';
    } catch (err) {
        return '';
    }
}

export default function ImportPurchaseStories() {
    const { centralCompanyId, purchaseId } = useParams();
    const { state } = useLocation();
    // The purchase list passes the purchase along; it is fetched only on a direct visit or refresh.
    const navigationPurchase = state?.purchase?._id === purchaseId ? state.purchase : null;
    const [ purchase, setPurchase ] = useState(null);
    const [ purchaseError, setPurchaseError ] = useState('');
    const [ page, setPage ] = useState(0);
    const [ attempt, setAttempt ] = useState(0);
    const [ stories, setStories ] = useState([]);
    const [ loading, setLoading ] = useState(true);
    const [ error, setError ] = useState('');
    const [ selected, setSelected ] = useState(() => new Map());
    const [ importing, setImporting ] = useState(false);
    const [ sessionExpired, setSessionExpired ] = useState(false);

    const expireSession = () => {
        cookie.delete_cookie(process.env.REACT_APP_COOKIE_NAME);
        cookie.delete_cookie('loginProfileInfo');
        AuthObject.isAuthenticated = false;
        setSessionExpired(true);
    };

    // Importing is only allowed into the company's published purchase.
    useEffect(() => {
        const controller = new AbortController();
        setPurchaseError('');
        if (navigationPurchase && attempt === 0) {
            if (navigationPurchase.status === PURCHASE_STATUS.PUBLISHED && navigationPurchase.origin) setPurchase(navigationPurchase);
            else setPurchaseError('Stories can only be imported into a published purchase.');
            return () => controller.abort();
        }
        getPurchase(purchaseId, controller.signal)
            .then(match => {
                if (controller.signal.aborted) return;
                if (String(match.centralCompanyId) !== String(centralCompanyId)
                    || match.status !== PURCHASE_STATUS.PUBLISHED || !match.origin) {
                    setPurchase(null);
                    setPurchaseError('Stories can only be imported into a published purchase.');
                    return;
                }
                setPurchase(match);
            })
            .catch(err => {
                if (controller.signal.aborted) return;
                if (isAuthError(err)) expireSession();
                else setPurchaseError('Unable to load purchase. Please try again.');
            });
        return () => controller.abort();
    }, [ centralCompanyId, purchaseId, attempt ]); // eslint-disable-line

    useEffect(() => {
        if (!purchase) return undefined;
        const controller = new AbortController();
        setLoading(true);
        setError('');
        getAllCompanyStories({ centralCompanyId, origin: purchase.origin }, controller.signal)
            .then(all => {
                if (controller.signal.aborted) return;
                const seen = new Set();
                setStories(all.filter(story => {
                    if (seen.has(String(story.storyId)) || importBlockReason(story, purchase, centralCompanyId)) return false;
                    seen.add(String(story.storyId));
                    return true;
                }));
            })
            .catch(err => {
                if (controller.signal.aborted) return;
                if (isAuthError(err)) expireSession();
                else setError('Unable to load stories. Please try again.');
            })
            .finally(() => { if (!controller.signal.aborted) setLoading(false); });
        return () => controller.abort();
    }, [ purchase, centralCompanyId ]); // eslint-disable-line

    const total = stories.length;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const currentPage = Math.min(page, totalPages - 1);
    const pageStories = stories.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);

    const toggle = story => {
        const reason = importBlockReason(story, purchase, centralCompanyId);
        if (reason) { toastNotify(3, reason); return; }
        if (!selected.has(story.storyId) && selected.size >= MAX_SELECTION) {
            toastNotify(3, `You can import up to ${MAX_SELECTION} stories at a time.`);
            return;
        }
        setSelected(current => {
            const next = new Map(current);
            if (next.has(story.storyId)) next.delete(story.storyId);
            else next.set(story.storyId, story);
            return next;
        });
    };

    const pageIds = pageStories.map(story => story.storyId);
    const allOnPageSelected = pageIds.length > 0 && pageIds.every(id => selected.has(id));
    const togglePage = () => {
        setSelected(current => {
            const next = new Map(current);
            if (allOnPageSelected) pageIds.forEach(id => next.delete(id));
            else pageStories.forEach(story => { if (next.size < MAX_SELECTION) next.set(story.storyId, story); });
            return next;
        });
    };

    const importSelected = async () => {
        if (importing || !selected.size) return;
        const createdBy = currentUser();
        if (!createdBy) { expireSession(); return; }
        const blocked = [ ...selected.values() ].filter(story => importBlockReason(story, purchase, centralCompanyId));
        if (blocked.length) {
            setSelected(current => new Map([ ...current ].filter(([ , story ]) => !importBlockReason(story, purchase, centralCompanyId))));
            toastNotify(3, `${blocked.length} selected ${blocked.length === 1 ? 'story was' : 'stories were'} removed because ${blocked.length === 1 ? 'it' : 'they'} cannot be imported into this purchase.`);
            return;
        }
        setImporting(true);
        try {
            const response = await bulkTransferStories(purchaseId, [ ...selected.keys() ], createdBy);
            if (response.failed) toastNotify(3, `${response.transferred} of ${response.total} imported. ${response.failed} failed and are still selected.`);
            else toastNotify(1, `${response.transferred} ${response.transferred === 1 ? 'story' : 'stories'} imported`);
            // Keep only the failed stories selected so they can be retried.
            const failedIds = new Set((response.results || []).filter(item => item.status === 'failed').map(item => item.storyId));
            setSelected(current => new Map([ ...current ].filter(([ id ]) => failedIds.has(id))));
            setAttempt(value => value + 1);
        } catch (err) {
            if (isAuthError(err)) expireSession();
            else toastNotify(3, err.response?.data?.status?.message || err.message || 'Unable to import stories. Please try again.');
        } finally {
            setImporting(false);
        }
    };

    if (sessionExpired) return <Navigate to="/" replace />;

    const toShowPublishType = pageStories.some(story => story.storyType === 2);
    return (
        <div className="bodyWrapper">
            <Header />
            <section className="listing_header">
                <h1> Import Stories</h1>
                <div>
                    <Link to={`/companies/${centralCompanyId}/purchases`}>← Back to purchases</Link>
                    <p>Purchase ID: {purchaseId}</p>
                </div>
                <div className="wrapper">
                    {purchaseError && <div role="alert">
                        <p>{purchaseError}</p>
                        <button type="button" onClick={() => setAttempt(value => value + 1)}>Retry</button>
                    </div>}
                    {!purchase && !purchaseError && <p role="status">Loading purchase…</p>}
                    {purchase && loading && <p role="status">Loading stories…</p>}
                    {purchase && !loading && error && <div role="alert">
                        <p>{error}</p>
                        <button type="button" onClick={() => setAttempt(value => value + 1)}>Retry</button>
                    </div>}
                    {purchase && !loading && !error && <div className="wrapperTable">
                        <div className="wrapperForm">
                            <div className="form-group">
                                <span>{selected.size} selected</span>
                                <button type="button" className="btn-default" disabled={!selected.size || importing} onClick={() => setSelected(new Map())}>Clear</button>
                                <button type="button" className="btn-default color_green" disabled={!selected.size || importing} onClick={importSelected}>
                                    {importing ? 'Importing…' : 'Import'}
                                </button>
                            </div>
                        </div>
                        <table border="1" className="mtb20 recruiter_listing" width="100%">
                            <tbody>
                                <tr className="table-header">
                                    <th className="table_header">
                                        <input type="checkbox" checked={allOnPageSelected} onChange={togglePage} disabled={importing || !pageStories.length} />
                                    </th>
                                    <th className="table_header">Story ID</th>
                                    <th className="table_header">Story</th>
                                    <th className="table_header">Thumbnail</th>
                                    {toShowPublishType && (<th className="table_header ">Publish Type</th>)}
                                    <th className="table_header">Status</th>
                                    <th className="table_header">Published On</th>
                                    <th className="table_header">Created On</th>
                                    <th className="table_header">Expiry</th>
                                    <th className="table_header">Claps</th>
                                    <th className="table_header">Views</th>
                                    <th className="table_header">Swipe Up</th>
                                </tr>
                                {pageStories.map(item => (
                                    <tr key={item.storyId}>
                                        <td>
                                            <input type="checkbox" checked={selected.has(item.storyId)} disabled={importing} onChange={() => toggle(item)} />
                                        </td>
                                        <td>
                                            <p>{item.storyId}</p>
                                        </td>
                                        <td>
                                            <div className={item.storyType == 2 ? 'videoComponent' : 'videoComponent hidden'}>
                                                <video width="200" height="200" controls key={item.s3Path}>
                                                    <source src={item.s3Path} type="video/mp4" />
                                                </video>
                                            </div>
                                            <div className={item.storyType == 2 ? 'imageComponent hidden' : 'imageComponent'}>
                                                <img src={item.s3Path} alt="" />
                                            </div>
                                        </td>
                                        <td>
                                            {item.thumbnailUrl && <img src={item.thumbnailUrl} alt="Thumbnail" style={{ width: '190px', height: '200px' }} />}
                                        </td>
                                        {toShowPublishType && (
                                            <td>
                                                <p>{item.publishType == 2 ? 'reel' : 'story'}</p>
                                            </td>
                                        )}
                                        <td>
                                            <p>{STORY_STATUS[ item.status ]}</p>
                                        </td>
                                        <td>
                                            <p>{item.publishOn}</p>
                                        </td>
                                        <td>
                                            <p>{calculateDateInFormat(item.createdOn)}</p>
                                        </td>
                                        <td>
                                            <p>{calculateDateInFormat(item.expiryOn)}</p>
                                        </td>
                                        <td>
                                            <p>{item.totalClapCount}</p>
                                        </td>
                                        <td>
                                            <p>{item.totalViewCount}</p>
                                        </td>
                                        <td>
                                            <p>{item.totalScrollCount}</p>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {!total && <EmptyScreen item="Stories" />}
                        {total > PAGE_SIZE && <nav aria-label="Story pagination">
                            <button type="button" disabled={importing || currentPage === 0} onClick={() => setPage(currentPage - 1)}>Previous</button>
                            <span> Page {currentPage + 1} of {totalPages} · {total} stories </span>
                            <button type="button" disabled={importing || currentPage + 1 >= totalPages} onClick={() => setPage(currentPage + 1)}>Next</button>
                        </nav>}
                    </div>}
                </div>
            </section>
            <Footer />
        </div>
    );
}
